import type { BlogPost, Industry, Product, Project, Service, Solution } from "@latam-energy/types";

export const services: Service[] = [
  {
    slug: "automatizacion-industrial",
    title: "Automatización industrial",
    summary: "Control, supervisión e integración para procesos industriales más confiables.",
    description:
      "Diseñamos arquitecturas de automatización industrial orientadas a continuidad operativa, mantenibilidad y crecimiento por etapas.",
    icon: "cpu",
    deliverables: ["Arquitectura de control", "Tableros y señales", "Puesta en marcha", "Documentación técnica"],
    applications: ["Líneas de producción", "Plantas de proceso", "Skids", "Servicios auxiliares"],
    faq: [
      {
        question: "Pueden modernizar sistemas existentes?",
        answer: "Sí. La evaluación inicia con levantamiento técnico y definición de riesgos de migración."
      }
    ],
    seoTitle: "Automatización industrial en Perú | Latam Energy Services",
    seoDescription: "Servicios de automatización industrial, control y supervisión para plantas en Perú y Latinoamérica."
  },
  {
    slug: "instrumentacion-control",
    title: "Instrumentación y control",
    summary: "Selección, instalación y calibración de instrumentos para variables críticas.",
    description:
      "Acompañamos proyectos de presión, temperatura, flujo, nivel y analítica con criterios técnicos, documentación y soporte de campo.",
    icon: "gauge",
    deliverables: ["Listas de instrumentos", "Hojas de datos", "Loop checks", "Soporte de calibración"],
    applications: ["Medición de proceso", "Seguridad operativa", "Monitoreo de calidad", "Servicios industriales"],
    faq: [],
    seoTitle: "Instrumentación y control industrial | Latam Energy Services",
    seoDescription: "Instrumentación industrial para medición, control de procesos y mantenimiento técnico."
  },
  {
    slug: "soluciones-electricas",
    title: "Soluciones eléctricas",
    summary: "Ingeniería eléctrica, tableros, protección y distribución para instalaciones industriales.",
    description:
      "Desarrollamos soluciones eléctricas para baja y media tensión, integrando seguridad, continuidad y facilidad de mantenimiento.",
    icon: "zap",
    deliverables: ["Diagramas unifilares", "Tableros", "Protecciones", "Pruebas eléctricas"],
    applications: ["Subestaciones", "MCC", "Distribución", "Cargas críticas"],
    faq: [],
    seoTitle: "Soluciones eléctricas industriales | Latam Energy Services",
    seoDescription: "Ingeniería eléctrica e instrumentación para operaciones industriales y energía."
  },
  {
    slug: "eficiencia-energetica",
    title: "Eficiencia energética",
    summary: "Diagnóstico, medición y mejora del consumo energético industrial.",
    description:
      "Identificamos oportunidades de ahorro y control mediante medición, variadores, monitoreo y mejores prácticas operativas.",
    icon: "leaf",
    deliverables: ["Línea base", "Medición energética", "Plan de mejora", "Indicadores"],
    applications: ["Motores", "Aire comprimido", "Bombas", "Sistemas térmicos"],
    faq: [],
    seoTitle: "Eficiencia energética industrial | Latam Energy Services",
    seoDescription: "Soluciones de energía para la industria, monitoreo y mejora del consumo energético."
  },
  {
    slug: "integracion-sistemas",
    title: "Integración de sistemas",
    summary: "Conectividad entre PLC, SCADA, sensores, energía y datos operativos.",
    description:
      "Integramos sistemas de control y monitoreo con criterios de interoperabilidad, trazabilidad y ciberseguridad básica.",
    icon: "network",
    deliverables: ["Mapeo de señales", "Interfaces", "Históricos", "Pruebas FAT/SAT"],
    applications: ["SCADA", "Telemetría", "Reportes", "Control distribuido"],
    faq: [],
    seoTitle: "Integración de sistemas de control | Latam Energy Services",
    seoDescription: "Integración de sistemas de control industrial, SCADA, adquisición de datos y monitoreo."
  },
  {
    slug: "mantenimiento-industrial",
    title: "Mantenimiento industrial",
    summary: "Soporte técnico para equipos de automatización, instrumentación y energía.",
    description:
      "Planificamos mantenimientos preventivos y correctivos para reducir paradas y mejorar la confiabilidad de activos críticos.",
    icon: "wrench",
    deliverables: ["Inspección", "Diagnóstico", "Correctivos", "Informe técnico"],
    applications: ["Plantas continuas", "Equipos rotativos", "Tableros", "Instrumentación"],
    faq: [],
    seoTitle: "Mantenimiento industrial en Perú | Latam Energy Services",
    seoDescription: "Mantenimiento industrial para automatización, instrumentación, energía y control de procesos."
  }
];

export const products: Product[] = [
  ["posicionador-siemens-sipart-ps2", "Posicionador SIEMENS", "Instrumentación", "Posicionadores", "SIEMENS", "SIPART PS2", true, "/assets/industrial/siemens-pos.jpg"],
  ["valvula-polipropileno-pph", "Válvulas de polipropileno PPH", "Válvulas", "Válvulas termoplásticas", "DJK", "PPH", false, "/assets/industrial/valvula-pp-1.png"],
  ["transmisor-presion-proceso", "Transmisor de presión industrial", "Presión", "Instrumentación", "Marca referencial", "PT-400", true, "/assets/industrial/trans-pres.jpg"],
  ["flujometro-electromagnetico", "Flujómetro electromagnético", "Flujo", "Instrumentación", "Marca referencial", "FM-220", true, "/assets/industrial/fluj-elect.jpg"],
  ["valvula-mariposa", "Válvula mariposa", "Válvulas", "Control de procesos", "FISHER", "BV-30", true, "/assets/industrial/mariposa-valve.png"],
  ["valvula-control", "Válvula de control", "Válvulas", "Control de procesos", "FISHER", "CV-25", true, "/assets/industrial/valv-control.png"],
  ["termocupla-industrial", "Termocupla industrial", "Temperatura", "Instrumentación", "Marca referencial", "TC-K100", true, "/assets/industrial/termocupla.jpg"],
  ["sensor-temperatura-rtd", "Sensor RTD para proceso", "Temperatura", "Instrumentación", "Marca referencial", "RTD-100", false, "/assets/industrial/automation-controls.jpg"],
  ["transmisor-nivel-ultrasonico", "Transmisor de nivel ultrasónico", "Nivel", "Instrumentación", "Marca referencial", "LT-80", false, "/assets/industrial/valves-pumps.jpg"],
  ["plc-compacto-industrial", "PLC compacto industrial", "Automatización", "Controladores", "SIEMENS", "PLC-CX", false, "/assets/industrial/automation-controls.jpg"],
  ["hmi-panel-operador", "Panel HMI industrial", "Automatización", "Controladores", "Marca referencial", "HMI-10", false, "/assets/industrial/automation-controls.jpg"],
  ["variador-velocidad-bomba", "Variador de velocidad para bombas", "Variadores de velocidad", "Soluciones eléctricas", "YASKAWA", "VFD-P", false, "/assets/industrial/elevator-service.jpg"],
  ["medidor-energia-trifasico", "Medidor de energía trifásico", "Monitoreo energético", "Soluciones eléctricas", "Marca referencial", "EM-3P", false, "/assets/industrial/automation-controls.jpg"],
  ["sensor-proximidad-inductivo", "Sensor de proximidad inductivo", "Sensores", "Automatización", "Marca referencial", "IS-18", false, "/assets/industrial/automation-controls.jpg"],
  ["fuente-poder-industrial", "Fuente de poder industrial 24 VDC", "Soluciones eléctricas", "Tableros", "Marca referencial", "PS-24", false, "/assets/industrial/elevator-service.jpg"],
  ["registrador-datos-industrial", "Registrador de datos industrial", "Monitoreo y datos", "Adquisición de datos", "Marca referencial", "DL-8", false, "/assets/industrial/automation-controls.jpg"],
  ["manifold-instrumentacion", "Manifold para instrumentación", "Presión", "Accesorios", "Marca referencial", "MF-5", false, "/assets/industrial/valves-pumps.jpg"],
  ["arrancador-suave-motor", "Arrancador suave para motor", "Soluciones eléctricas", "Control de motores", "WEG", "SS-55", false, "/assets/industrial/elevator-service.jpg"],
  ["switch-industrial-ethernet", "Switch industrial Ethernet", "Automatización", "Comunicaciones", "Marca referencial", "SW-8", false, "/assets/industrial/automation-controls.jpg"],
  ["modulo-io-remoto", "Módulo I/O remoto", "Automatización", "Controladores", "Marca referencial", "RIO-16", false, "/assets/industrial/automation-controls.jpg"]
].map(([slug, name, category, subcategory, brand, model, featured, mainImage]) => ({
  slug: String(slug),
  name: String(name),
  shortDescription: `Equipo referencial para ${String(category).toLowerCase()} en aplicaciones industriales.`,
  description:
    "Producto genérico para proyectos de automatización, instrumentación y energía. La selección final debe validarse según rango, materiales, ambiente, señales y normas del proyecto.",
  category: String(category),
  subcategory: String(subcategory),
  brand: String(brand),
  model: String(model),
  applications: ["Minería", "Manufactura", "Energía", "Agua y utilities"],
  features: ["Construcción industrial", "Integración con sistemas de control", "Documentación técnica", "Soporte para selección"],
  downloads: [{ label: "Ficha técnica referencial", href: "/descargas/ficha-referencial.pdf", type: "ficha-tecnica" }],
  mainImage: String(mainImage),
  gallery: String(slug) === "valvula-polipropileno-pph"
    ? ["/assets/industrial/valvula-pp-1.png", "/assets/industrial/valvula-pp-2.png", "/assets/industrial/mariposa-valve.png", "/assets/industrial/valvula-pp-3.png", "/assets/industrial/valvula-pp-4.png"]
    : [String(mainImage)],
  seoTitle: `${String(name)} | Catálogo Latam Energy Services`,
  seoDescription: `${String(name)} para proyectos industriales. Solicita asesoría técnica y cotización.`,
  featured: Boolean(featured)
}));

export const industries: Industry[] = [
  ["petroleo-gas", "Petróleo y gas", "Soluciones certificadas para el sector industrial más exigente y crítico."],
  ["mineria", "Minería", "Soluciones robustas para ambientes exigentes: bombeo, medición y control."],
  ["manufactura", "Manufactura", "Control de Procesos, Eficiencia y Mantenimiento"],
  ["energia", "Energía", "Soluciones para aplicaciones críticas de alta presión y temperatura."],
  ["agua-utilities", "Agua, Riego y Residuales", "Telemetría, bombeo, nivel, flujo y control para infraestructura crítica."],
  ["infraestructura", "Edificios e infraestructura", "Integración eléctrica, control y monitoreo para aplicaciones domiciliarias y HVAC."]
].map(([slug, name, summary]) => ({
  slug,
  name,
  summary,
  needs: ["Continuidad operativa", "Medición confiable", "Mantenimiento planificado", "Integración de datos"],
  relatedServices: ["automatizacion-industrial", "instrumentacion-control", "soluciones-electricas"],
  seoTitle: `${name} | Soluciones industriales Latam Energy Services`,
  seoDescription: `${summary} Servicios de ingeniería, automatización y energía.`
}));

export const solutions: Solution[] = [
  {
    slug: "monitoreo-adquisicion-datos",
    title: "Monitoreo y adquisición de datos",
    summary: "Arquitecturas para visualizar variables críticas, historizar información y habilitar decisiones operativas.",
    components: ["Sensores", "PLC o RTU", "Red industrial", "SCADA", "Reportes"],
    industries: ["mineria", "agua-utilities", "energia"],
    seoTitle: "Monitoreo y adquisición de datos industriales",
    seoDescription: "Soluciones de monitoreo industrial, telemetría y adquisición de datos para operaciones técnicas."
  },
  {
    slug: "control-procesos",
    title: "Control de procesos",
    summary: "Instrumentación, válvulas y lógica de control para estabilizar variables de proceso.",
    components: ["Instrumentos", "Válvulas", "Controladores", "HMI", "Pruebas"],
    industries: ["petroleo-gas", "manufactura", "mineria"],
    seoTitle: "Control de procesos industriales",
    seoDescription: "Control de procesos para presión, temperatura, flujo, nivel y variables industriales."
  },
  {
    slug: "energia-motores",
    title: "Energía y control de motores",
    summary: "Tableros, variadores, medición y protección para cargas industriales.",
    components: ["VFD", "MCC", "Medidores", "Protecciones", "Tableros"],
    industries: ["manufactura", "agua-utilities", "infraestructura"],
    seoTitle: "Control eléctrico y energía para motores industriales",
    seoDescription: "Soluciones eléctricas para motores, variadores, medición energética y distribución industrial."
  }
];

export const projects: Project[] = [
  {
    slug: "modernizacion-bombeo-industrial",
    title: "Modernización de sistema de bombeo industrial",
    summary: "Caso referencial de automatización y control de bombas con medición energética.",
    industry: "agua-utilities",
    scope: ["Levantamiento", "Tablero de control", "Variadores", "Pruebas funcionales"],
    result: "Mejor visibilidad operativa y base técnica para optimización energética.",
    seoTitle: "Proyecto referencial de bombeo industrial",
    seoDescription: "Aplicación referencial de automatización, variadores y monitoreo para bombeo industrial."
  },
  {
    slug: "instrumentacion-linea-proceso",
    title: "Instrumentación para línea de proceso",
    summary: "Selección referencial de medición de presión, flujo y temperatura para una línea productiva.",
    industry: "manufactura",
    scope: ["Hojas de datos", "Instrumentos", "Loop checks", "Documentación"],
    result: "Variables críticas organizadas para operación y mantenimiento.",
    seoTitle: "Proyecto referencial de instrumentación de proceso",
    seoDescription: "Aplicación de instrumentación y control industrial para manufactura."
  }
];

export const blogPosts: BlogPost[] = [
  {
    slug: "como-elegir-instrumentacion-industrial",
    title: "Cómo elegir instrumentación industrial para una planta",
    category: "Instrumentación",
    date: "2026-07-15",
    readingTime: "5 min",
    summary: "Criterios practicos para selecciónar instrumentos según proceso, ambiente y mantenimiento.",
    content: [
      "La selección de instrumentación debe partir de la variable a medir, el rango operativo y las condiciones de instalación.",
      "También conviene revisar materiales, conexión de proceso, señal disponible, alimentación y documentación requerida."
    ],
    seoTitle: "Cómo elegir instrumentación industrial",
    seoDescription: "Guía en español para elegir instrumentación industrial en proyectos de control de procesos."
  },
  {
    slug: "eficiencia-energetica-motores",
    title: "Eficiencia energética en motores industriales",
    category: "Energía",
    date: "2026-07-08",
    readingTime: "4 min",
    summary: "Medición, variadores y mantenimiento como base para reducir desperdicios energéticos.",
    content: [
      "Los motores concentran una parte importante del consumo eléctrico en muchas operaciones industriales.",
      "Medir, controlar velocidad y mantener condiciones mecanicas ayuda a construir una estrategia realista."
    ],
    seoTitle: "Eficiencia energética en motores industriales",
    seoDescription: "Prácticas de eficiencia energética industrial para motores, variadores y monitoreo."
  },
  {
    slug: "integracion-scada-datos",
    title: "Integración SCADA y datos operativos",
    category: "Automatización",
    date: "2026-06-28",
    readingTime: "6 min",
    summary: "Cómo conectar señales de campo con supervisión, historicos y reportes utiles.",
    content: [
      "Una integración SCADA efectiva define señales, criticidad, frecuencias de adquisición y usuarios finales.",
      "El valor aparece cuando los datos operativos son confiables, trazables y faciles de interpretar."
    ],
    seoTitle: "Integración SCADA y datos operativos",
    seoDescription: "Conceptos para integración de sistemas SCADA, monitoreo y adquisición de datos."
  }
];

export const brands = ["Marca referencial A", "Marca referencial B", "Marca referencial C", "Marca referencial D", "Marca referencial E"];

export const downloads = [
  { label: "Perfil corporativo referencial", href: "/descargas/perfil-corporativo.pdf", type: "brochure" },
  { label: "Catálogo técnico referencial", href: "/descargas/catalogo-tecnico.pdf", type: "catalogo" },
  { label: "Ficha técnica referencial", href: "/descargas/ficha-referencial.pdf", type: "ficha-tecnica" }
] as const;
